import { Button, CommonModalProps, Container, RadioButtons } from "@animech/ui";
import {
	FunctionComponent,
	HTMLAttributes,
	useCallback,
	useState,
} from "react";
import { GlobeIcon, useUiData } from "src/ui";
import { Locale, Term } from "@definitions";
import { ModalBase } from "src/ui/components/modals/modal-base";
import { locales } from "src/definitions/locale-definitions";

export type LanguageModalProps = CommonModalProps<
	HTMLAttributes<HTMLDivElement>
>;

type RadioButtonsData = {
	items: Locale[];
	itemLabels: string[];
};
type LanguageModalTermKeys = "title" | "confirm" | "text";

const terms: { [key in LanguageModalTermKeys]: Term } = {
	title: "languageModal:title",
	confirm: "languageModal:confirm",
	text: "languageModal:text",
};

export const LanguageModal: FunctionComponent<LanguageModalProps> = ({
	...baseProps
}) => {
	const { onShouldClose } = baseProps;
	const { localizer } = useUiData();
	const [locale, setLocale] = useState<string>(localizer.locale);

	const onClose = useCallback(
		(updateLocale: boolean) => {
			if (!onShouldClose) {
				return;
			}

			if (updateLocale === true) {
				onShouldClose(locale);
				return;
			}

			onShouldClose(undefined);
		},
		[locale, onShouldClose],
	);

	const { items, itemLabels } = locales.reduce(
		(acc, locale) => {
			acc.items.push(locale);
			acc.itemLabels.push(localizer.localizeTerm(locale));
			return acc;
		},
		{ items: [], itemLabels: [] } as RadioButtonsData,
	);

	return (
		<ModalBase
			{...baseProps}
			icon={<GlobeIcon />}
			title={localizer.localizeTerm(terms.title)}
			footerButtons={[
				<Button key="footerButton" onClick={() => onClose(true)}>
					{localizer.localizeTerm(terms.confirm)}
				</Button>,
			]}
			onShouldClose={onClose}
		>
			<Container>
				<p>{localizer.localizeTerm(terms.text)}</p>

				<RadioButtons
					color="neutral"
					appearance="ghost"
					itemLabels={itemLabels}
					items={items}
					onValueChange={setLocale}
					value={locale as Locale}
				></RadioButtons>
			</Container>
		</ModalBase>
	);
};
